// src/components/editions/editionCopy.ts
//
// Every customer-facing string of the edition UI (plan 2026-10-23-an-edition-
// is-an-argument-keyed-snapshot, C6), in one place so the drawer, the button
// and the edition page say the same thing. A string with a number in it is a
// function, so the sentence stays whole.
import type { EditionBuildRefusal, EditionStatus } from "@/api/editions";

export const EDITION_COPY = {
  drawerTitle: "Editions",
  buildButton: "Build edition",
  editionsButton: "Editions",
  empty: "No editions yet. Build edition freezes this report's numbers for its period.",
  loadError: "The editions could not be loaded.",
  tryAgain: "Try again",
  loadMore: "Load more",
  preview: "Preview",
  confirmTitle: "Build an edition?",
  confirmBody:
    "Verity freezes this report's numbers for its period, as the report stands now. " +
    "This edition is a preview: only people who can edit the report see it.",
  confirmLabel: "Build edition",
  cancel: "Cancel",
  started: "Building the edition. It appears under Editions when it is ready.",
  inProgress: "An edition of this report is being built. Open Editions to follow it.",
  enqueueFailed: "The edition could not be started. Try again in a minute.",
  windowEmpty:
    "This report's period has no complete day yet. Build the edition tomorrow, or choose a period that has ended.",
  buildFailed: "The edition could not be started.",
  backToReport: "Back to report",
  notFound: "This edition does not exist, or you cannot see it.",
  pageLoadError: "The edition could not be loaded.",
  editionFor: (label: string, range: string): string => (label === range ? `Edition for ${range}` : `Edition for ${label} (${range})`),
} as const;

export const EDITION_STATUS_LABEL: Record<EditionStatus, string> = {
  QUEUED: "Building",
  RUNNING: "Building",
  READY: "Ready",
  PARTIAL: "Partly built",
  FAILED: "Failed",
};

export const EDITION_STATUS_COLOR: Record<EditionStatus, string> = {
  QUEUED: "gray",
  RUNNING: "gray",
  READY: "teal",
  PARTIAL: "yellow",
  FAILED: "red",
};

function count(n: number, word: string): string {
  return `${n} ${word}${n === 1 ? "" : "s"}`;
}

/** A PARTIAL row: which blocks are missing and why, by count (the list
 *  carries counts; the edition page names the blocks). */
export function partialSummary(counts: { refused: number; failed: number }): string {
  const parts: string[] = [];
  if (counts.refused > 0) parts.push(`${count(counts.refused, "block")} without data access`);
  if (counts.failed > 0) parts.push(`${count(counts.failed, "block")} could not be built`);
  return parts.join(" · ");
}

export function versionLabel(versionNumber: number | null | undefined): string | null {
  return versionNumber == null ? null : `Version ${versionNumber}`;
}

export function refusalMessage(refusal: EditionBuildRefusal): string {
  switch (refusal.code) {
    case "edition_in_progress":
      return EDITION_COPY.inProgress;
    case "edition_cap_reached":
      return refusal.cap === "PREVIEW_BUILDS_PER_USER_PER_DAY"
        ? `You have built ${count(refusal.limit, "preview")} today, the daily limit. Try again tomorrow.`
        : `This account has built ${count(refusal.limit, "edition")} today, the daily limit. Try again tomorrow.`;
    case "edition_too_large":
      return `This report runs ${refusal.queries} ${refusal.queries === 1 ? "query" : "queries"} ` +
        `and one edition can run ${refusal.limit}. ` +
        "Remove blocks or split the report.";
    case "edition_window_empty":
      return EDITION_COPY.windowEmpty;
    case "edition_enqueue_failed":
      return EDITION_COPY.enqueueFailed;
  }
}
